import { or } from 'tio-ui/utilities';
import { t } from 'ember-intl';
import Component from '@glimmer/component';
import ReCheckbox from 'tio-reimagined/components/checkbox';
import CheckboxList from 'tio-reimagined/components/checkbox-list';
import ReSelect from 'tio-reimagined/components/select';
import type { PaymentFilterSignature as S } from '../types.gts';

function getYearsFormatted(numberOfYears: number, monthOffset?: number | null) {
  const formattedYears = [];
  const now = new Date();
  let startYear = now.getFullYear();

  const shouldIncrementYear =
    monthOffset !== undefined && monthOffset !== null && now.getMonth() + 1 >= monthOffset;
  if (shouldIncrementYear) {
    startYear += 1;
  }

  for (let i = 0; i < numberOfYears; i++) {
    formattedYears.push(`${startYear - i}`);
  }

  return formattedYears;
}

interface PaymentFilterSignature {
  Args: S & {
    paymentYear?: string;
    monthOffset?: number;
    onFilterUpdate: (filterUpdates: Record<string, unknown>) => void;
  };
}

export default class PaymentFilter extends Component<PaymentFilterSignature> {
  get selectedPaymentKeys(): string[] {
    return this.args.paymentStates ? this.args.paymentStates.split(',').filter(Boolean) : [];
  }

  get paymentFieldItems(): { key: string; label: string }[] {
    return [
      {
        key: 'tas_application_payment_final_approved_total_amount_integer',
        label: 'Approved Total',
      },
      { key: 'tas_application_payment_pending_amount_integer', label: 'Pending Amount' },
      { key: 'tas_application_payment_paid_amount_integer', label: 'Paid Amount' },
      { key: 'tas_application_payment_last_date_date', label: 'Payment Date' },
      {
        key: 'tas_application_payment_refund_requested_amount_integer',
        label: 'Refund Requested Amount',
      },
      {
        key: 'tas_application_payment_refund_paid_back_amount_integer',
        label: 'Refund Paid Back Amount',
      },
      {
        key: 'tas_application_payment_refund_forgiven_amount_integer',
        label: 'Refund Forgiven Amount',
      },
      {
        key: 'tas_application_payment_refund_cancelled_amount_integer',
        label: 'Refund Cancelled Amount',
      },
      { key: 'tas_application_payment_refund_limit_amount_integer', label: 'Refund Limit Amount' },
    ];
  }

  get yearOptions() {
    return getYearsFormatted(5, this.args.monthOffset).map((y) => ({ key: y, label: y }));
  }

  includePaymentsDidChange = (checked: boolean) => {
    this.args.onFilterUpdate({ includePayments: checked, paymentStates: '', paymentYear: '' });
  };

  paymentFieldsDidChange = (selectedKeys: string[]) => {
    this.args.onFilterUpdate({ paymentStates: selectedKeys.join(',') });
  };

  paymentYearDidChange = (key: string | null) => {
    this.args.onFilterUpdate({ paymentYear: key ?? '' });
  };

  <template>
    <ReCheckbox
      @label={{t "common.reporting.include_payment_details"}}
      @checked={{or @includePayments false}}
      @onChange={{this.includePaymentsDidChange}}
      @size="sm"
    />

    {{#if @includePayments}}
      <div class="ml-4 flex flex-col gap-y-3">
        <CheckboxList
          @items={{this.paymentFieldItems}}
          @selectedKeys={{this.selectedPaymentKeys}}
          @onChange={{this.paymentFieldsDidChange}}
          @size="sm"
        />

        <ReSelect
          @label={{t "common.reporting.payment_fiscal_year"}}
          @items={{this.yearOptions}}
          @selected={{@paymentYear}}
          @onChange={{this.paymentYearDidChange}}
          @clearable={{true}}
          @placeholder="Select year"
          @size="sm"
        />
      </div>
    {{/if}}
  </template>
}
